import './EventCard.scss'

import {
	Card,
	CardContent,
	CardImage,
	CardImageOverlay,
	CardLink,
	CardText,
	CardTitle,
	CardType
} from '../../ui/Card'

import format from 'date-fns/format'
import isSameDay from 'date-fns/isSameDay'
import isValid from 'date-fns/isValid'
import parse from 'date-fns/parse'
import React from 'react'
import {switchLocale} from '../../../util/dates'
import {useTranslation} from '../../../util/i18n'
import {style} from '../../../util/style'
import {Icon} from '../../../views/ui/Icon'

const styles = style('eventcard', ['image', 'info', 'infoitem'])

export type EventCardType = CardType & EventCardInfoType

type EventCardInfoType = {
	location_zip?: string
	location_city?: string
	date_start?: string
	date_end?: string
}

export const EventCard: React.FC<EventCardType> = ({
	url,
	image,
	location_zip,
	location_city,
	date_end,
	date_start,
	title,
	text,
	linklabel
}) => {
	const t = useTranslation('cards').event
	return (
		<Card layout="event" className={styles()}>
			<CardImage image={image} url={url}>
				<CardImageOverlay />
				<span className={styles('overlay')} />
				<EventCardInfo
					location_zip={location_zip}
					location_city={location_city}
					date_end={date_end}
					date_start={date_start}
				/>
			</CardImage>
			<CardContent>
				<CardTitle title={title} url={url} />
				<CardText text={text} />
				<CardLink url={url} description={linklabel || t.linklabel} />
			</CardContent>
		</Card>
	)
}

const isDateValid = (dateStr: string): false | Date => {
	try {
		const parsedDate = parse(dateStr, 'yyyy-MM-dd HH:mm:ss', new Date())
		return isValid(parsedDate) ? parsedDate : false
	} catch (e) {
		return false
	}
}

const getDateStringRange = (dateStart: Date | null, dateEnd: Date | null) => {
	if (!dateStart) return null

	const start = format(dateStart, 'd MMMM', {
		locale: switchLocale()
	})
	if (!dateEnd) return start

	if (isSameDay(dateStart, dateEnd)) return start

	const end = format(dateEnd, 'd MMMM', {
		locale: switchLocale()
	})
	return end ? `${start} - ${end}` : start
}

const EventCardInfo: React.FC<EventCardInfoType> = ({
	location_zip,
	location_city,
	date_end,
	date_start
}) => {
	if (!location_city && !date_start) return null

	const dateString: string | null = getDateStringRange(
		isDateValid(date_start) || null,
		isDateValid(date_end) || null
	)

	return (
		<div className={styles.info()}>
			<div className={styles.info('items')}>
				{location_city && (
					<EventCardInfoItem
						icon="pin"
						label={location_zip + ' ' + location_city}
					/>
				)}
				{dateString && <EventCardInfoItem icon="calendar" label={dateString} />}
			</div>
		</div>
	)
}

const EventCardInfoItem = ({icon, label}) => (
	<div className={styles.infoitem()}>
		<span className={styles.infoitem('icon')}>
			<Icon icon={icon} />
		</span>
		<p className={styles.infoitem('label')}>{label}</p>
	</div>
)
